import * as Sentry from "@sentry/react";
import { useNavigate } from "@tanstack/react-router";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import {
  applyLiveNotificationEvent,
  NOTIFICATIONS_FEED_KEY,
  NOTIFICATIONS_UNREAD_KEY,
} from "@/site/components/notifications/notification-data";
import { BRAND_ORANGE } from "@shared/colors";
import { MY_CARDS_QUERY_KEY } from "@shared/my-cards-data";
import { createContext, useCallback, useContext, useEffect, useRef } from "react";

/**
 * Bell categories that mean a card changed state (sent, received, read, thanked, signed,
 * bounced). On one of these, My Cards refetches whatever tab is open, so a card that went
 * out moves from Upcoming to Sent without a reload. Reminders and Birthday Requests
 * don't touch My Cards.
 */
const MY_CARDS_CATEGORIES = new Set([
  "card_received",
  "send_confirmation",
  "signer_added",
  "sign_invite",
  "thank_you",
]);

interface WsMessage {
  type: string;
  data?: Record<string, unknown>;
}

type SendTypingFn = (conversationId: string) => void;

interface WebSocketContextValue {
  sendTyping: SendTypingFn;
  sendTypingEnd: SendTypingFn;
  /** Call when mounting a conversation thread so incoming messages for it don't flash the unread badge. */
  setActiveConversationId: (id: string | null) => void;
  /** Call on markRead success to clear the suppressed-unread counter. */
  clearShadowedUnread: () => void;
}

const WebSocketContext = createContext<WebSocketContextValue>({
  sendTyping: () => {},
  sendTypingEnd: () => {},
  setActiveConversationId: () => {},
  clearShadowedUnread: () => {},
});

export function WebSocketProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  // Keyed on WHO is signed in, not the `user` object itself — that's a fresh
  // reference on every `/me` invalidation and would reconnect the socket each time.
  const userId = user?.user_id ?? null;
  const navigate = useNavigate();
  // Read through a ref so `navigate` can't trigger a reconnect either.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectDelay = useRef(1000);
  // False until the first successful open; a later open is a RECONNECT, and any
  // notification broadcast during the gap was lost (no replay) — refetch then.
  const hasConnectedOnce = useRef(false);
  // Tracks which DM conversation is currently open so incoming messages for it
  // don't flash the unread badge — the user is already reading them.
  const activeConvIdRef = useRef<string | null>(null);
  // Counts how many unread increments we've suppressed for the active conversation.
  // Used to offset dm.unread_count events until markRead clears it.
  const shadowedUnreadRef = useRef(0);

  function wsSend(payload: Record<string, unknown>) {
    const ws = wsRef.current;
    if (ws?.readyState !== WebSocket.OPEN) return;
    const action = typeof payload.action === "string" ? payload.action : "";
    if (import.meta.env.DEV) {
      console.log(
        `%c-> WS %c${action}`,
        "color: #60b4ff; font-weight: bold",
        "color: #facc15",
        payload,
      );
    }
    ws.send(JSON.stringify(payload));
  }

  const sendTyping = useCallback((conversationId: string) => {
    wsSend({ action: "dm.typing", conversation_id: conversationId });
  }, []);

  const sendTypingEnd = useCallback((conversationId: string) => {
    wsSend({ action: "dm.typing.end", conversation_id: conversationId });
  }, []);

  useEffect(() => {
    if (!userId) return;

    function connect() {
      if (
        wsRef.current?.readyState === WebSocket.OPEN ||
        wsRef.current?.readyState === WebSocket.CONNECTING
      ) {
        return;
      }

      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const url = `${protocol}//${window.location.host}/ws?client=web`;

      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        if (import.meta.env.DEV) console.log("[WebSocket] Connection opened");
        reconnectDelay.current = 1000;
        if (hasConnectedOnce.current) {
          // Notifications that fired during the outage were lost (no replay) —
          // pull the feed + badge fresh, mirroring the DM refetch below.
          void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_FEED_KEY });
          void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_UNREAD_KEY });
        }
        hasConnectedOnce.current = true;
        const activeConv = activeConvIdRef.current;
        if (activeConv) {
          ws.send(JSON.stringify({ action: "subscribe_dm", dm_conversation_id: activeConv }));
          // Refetch the active conversation's messages — anything broadcast during the
          // WS gap was lost (no replay), so pull fresh from the server.
          queryClient.invalidateQueries({
            queryKey: ["/api/user/dm/conversations", activeConv, "messages"],
            refetchType: "active",
          });
        }
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(String(event.data)) as WsMessage;
          if (msg.type === "ping") {
            ws.send("pong");
            return;
          }
          if (msg.type === "subscribed") return;

          if (import.meta.env.DEV) {
            console.groupCollapsed(
              `%c<- WS %c${msg.type}`,
              "color: #60b4ff; font-weight: bold",
              `color: ${BRAND_ORANGE}; font-weight: bold`,
            );
            if (msg.data) console.log("data", msg.data);
            console.groupEnd();
          } else {
            Sentry.addBreadcrumb({
              category: "websocket",
              message: msg.type,
              level: "info",
            });
          }

          if (msg.type.startsWith("dm.")) {
            window.dispatchEvent(new CustomEvent("dm-event", { detail: msg }));
            if (msg.type === "dm.message.created") {
              const incomingConvId = msg.data?.conversation_id as string | undefined;
              const msgPayload = msg.data?.message as Record<string, unknown> | undefined;
              const isOwnMessage = String(msgPayload?.sender_user_id) === userId;

              if (!isOwnMessage && incomingConvId) {
                // Incoming message: update the conversations cache directly so the
                // unread badge appears immediately. Avoid invalidateQueries here —
                // the resulting HTTP refetch races against the DB update and can
                // return stale data (unread_count: 0), overwriting our update.
                const preview =
                  (msgPayload?.content as string | undefined)?.slice(0, 100) ?? "[Image]";
                const lastMessageAt = (msgPayload?.created_at as string | undefined) ?? null;
                // If the user is currently viewing this conversation, skip the unread
                // increment entirely — they're already reading the message. Track the
                // suppression so we can offset the dm.unread_count total as well.
                const isActiveConv = incomingConvId === activeConvIdRef.current;
                if (isActiveConv) shadowedUnreadRef.current += 1;
                type ConvRow = Record<string, unknown> & { dm_conversation_id: string };
                const bumpInInfinite = (
                  old: { pages: ConvRow[][]; pageParams: unknown[] } | undefined,
                ): { pages: ConvRow[][]; pageParams: unknown[] } | undefined => {
                  if (!old) return old;
                  const existing = old.pages
                    .flat()
                    .find((c) => c.dm_conversation_id === incomingConvId);
                  if (!existing) return old;
                  const updatedConv: ConvRow = {
                    ...existing,
                    unread_count: isActiveConv
                      ? existing.unread_count
                      : Number(existing.unread_count ?? 0) + 1,
                    last_message_preview: preview,
                    last_message_at: lastMessageAt ?? existing.last_message_at,
                  };
                  const pages = old.pages.map((page) =>
                    page.filter((c) => c.dm_conversation_id !== incomingConvId),
                  );
                  pages[0] = [updatedConv, ...(pages[0] ?? [])];
                  return { ...old, pages };
                };
                const bumpInArray = (old: ConvRow[] | undefined): ConvRow[] | undefined => {
                  if (!old) return old;
                  const existing = old.find((c) => c.dm_conversation_id === incomingConvId);
                  if (!existing) return old;
                  const updatedConv: ConvRow = {
                    ...existing,
                    unread_count: isActiveConv
                      ? existing.unread_count
                      : Number(existing.unread_count ?? 0) + 1,
                    last_message_preview: preview,
                    last_message_at: lastMessageAt ?? existing.last_message_at,
                  };
                  const rest = old.filter((c) => c.dm_conversation_id !== incomingConvId);
                  return [updatedConv, ...rest];
                };
                queryClient.setQueryData(["/api/user/dm/conversations"], bumpInInfinite);
                queryClient.setQueryData(["/api/user/dm/conversations", "requests"], bumpInArray);
                // If conversation wasn't in either cache (e.g. new message request),
                // fall back to a full refetch so it appears in the list.
                const inbox = queryClient.getQueryData<{ pages: ConvRow[][] }>([
                  "/api/user/dm/conversations",
                ]);
                const inInbox = !!inbox?.pages
                  ?.flat()
                  .some((c) => c.dm_conversation_id === incomingConvId);
                const inRequests = (
                  queryClient.getQueryData<ConvRow[]>(["/api/user/dm/conversations", "requests"]) ??
                  []
                ).some((c) => c.dm_conversation_id === incomingConvId);
                if (!inInbox && !inRequests) {
                  void queryClient.invalidateQueries({ queryKey: ["/api/user/dm/conversations"] });
                }
                // Do NOT invalidateQueries for unread-count here. The server sends a
                // dm.unread_count WS event immediately after dm.message.created with the
                // authoritative total. An invalidateQueries causes a redundant HTTP refetch
                // that can race and briefly overwrite the correct count with stale data.
              } else {
                // Own message sent: use a refetch to pick up the updated preview/order.
                void queryClient.invalidateQueries({
                  queryKey: ["/api/user/dm/conversations"],
                });
              }
            }
            if (msg.type === "dm.conversation.created" && msg.data) {
              // A new conversation was created with us as the target. It may land in
              // either the inbox (if we follow the sender) or the requests list.
              // Invalidate both so the correct list picks it up.
              void queryClient.invalidateQueries({
                queryKey: ["/api/user/dm/conversations"],
              });
            }
            if (msg.type === "dm.message.deleted" && msg.data?.preview_updated) {
              void queryClient.invalidateQueries({
                queryKey: ["/api/user/dm/conversations"],
              });
            }
            if (msg.type === "dm.unread_count" && msg.data?.count !== undefined) {
              const serverTotal = Number(msg.data.count);
              // Subtract any unread increments we suppressed for the active conversation
              // so the sidebar badge doesn't flash while the user is reading the thread.
              const displayTotal = Math.max(0, serverTotal - shadowedUnreadRef.current);
              queryClient.setQueryData(["/api/user/dm/conversations/unread-count"], {
                count: displayTotal,
              });
            }
            return;
          }

          // Stored notification feed (PRD 07 D8): server emits one event per
          // insert with the fresh unread total. Set the badge + refetch the
          // cached feed (the stored row is the truth — see
          // applyLiveNotificationEvent), and surface a transient toast so it's
          // noticed while the panel is closed.
          if (msg.type === "notification:new" && msg.data) {
            const d = msg.data as {
              title: string;
              body: string | null;
              link_path: string | null;
              unread_count: number;
              category?: string;
            };
            applyLiveNotificationEvent(d.unread_count);
            if (d.category && MY_CARDS_CATEGORIES.has(d.category)) {
              void queryClient.invalidateQueries({ queryKey: [MY_CARDS_QUERY_KEY] });
            }
            const linkPath = d.link_path;
            toast({
              variant: "info",
              title: d.title,
              description: d.body ?? undefined,
              // `href`, not `to`: link_path is a raw server-built path that may
              // carry its own query string ("?dt=…"), which `to` does not accept
              // (same rule as RedirectToHref in routes/__root.tsx).
              onClick: linkPath ? () => void navigateRef.current({ href: linkPath }) : undefined,
            });
            return;
          }
        } catch {
          /* suppress */
        }
      };

      ws.onclose = (event) => {
        if (import.meta.env.DEV) {
          console.log(
            `[WebSocket] Connection closed (code: ${event.code}, reason: ${event.reason || "none"})`,
          );
        }
        // Guard against stale close events: if this socket has already been
        // replaced (e.g. React StrictMode double-mount or effect re-run during
        // hot reload), do nothing. Without this check, the stale onclose nulls
        // wsRef.current (pointing to the newer socket) and schedules an extra
        // reconnect, accumulating one dead server-side connection per reload.
        if (wsRef.current !== ws) return;
        wsRef.current = null;
        if (event.code !== 4001) {
          reconnectTimer.current = setTimeout(() => {
            reconnectDelay.current = Math.min(reconnectDelay.current * 2, 30000);
            connect();
          }, reconnectDelay.current);
        }
      };

      ws.onerror = () => {
        ws.close();
      };
    }

    connect();

    return () => {
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [userId]);

  const setActiveConversationId = useCallback((id: string | null) => {
    const prev = activeConvIdRef.current;
    activeConvIdRef.current = id;
    if (!id) shadowedUnreadRef.current = 0;
    if (prev && prev !== id) {
      wsSend({ action: "unsubscribe_dm", dm_conversation_id: prev });
    }
    if (id && id !== prev) {
      wsSend({ action: "subscribe_dm", dm_conversation_id: id });
    }
  }, []);

  const clearShadowedUnread = useCallback(() => {
    shadowedUnreadRef.current = 0;
  }, []);

  return (
    <WebSocketContext
      value={{
        sendTyping,
        sendTypingEnd,
        setActiveConversationId,
        clearShadowedUnread,
      }}
    >
      {children}
    </WebSocketContext>
  );
}

export function useWebSocket() {
  return useContext(WebSocketContext);
}
